import React, { useContext, useEffect, useRef } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link } from "theme-ui";
import { useNavigate } from "react-router-dom";

import { createProvider } from "api/Providers";

import { CountryStates } from "helpers/CountryStates";
import { filterByKeys } from "helpers/default";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";


export default function ProviderCreate() {
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  const navigate = useNavigate();
  const toast = useToast();  
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const submitCreate = async (values: any) => {   
    const keys = ["name","phone","address.line1","address.line2","address.city","address.state","address.postalCode","contactEmail","billingEmail"];    
    const create = filterByKeys(keys,values);
    
    const res = await createProvider({ create });
    if(res?.id){ 
      toast.add("Provider created.","var(--theme-ui-colors-success)");   
      void trackActivity({
        module: "providers",
        action: "add_provider",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "provider",
        entityId: res.id,
        metadata: null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate(`/providers/${res.id}`);
    }else{
      toast.add(res?.message ? res.message : "Error creating provider","var(--theme-ui-colors-danger)");
    }                 
  };  

  const _validate = Yup.object().shape({
    name: Yup.string().required("Provider name is required."),    
  });


  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Provider";
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    void trackActivity({
      module: "providers",
      action: "view_provider_create",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user?.id, user?.company?.id]); 


  return (
    <>
      <Header></Header>      
      <Page 
        title="Add provider" 
        subtitle="Add a provider to your company. Once you save the provider, you will be able to add depots and service areas to the provider." 
        backAction={<Link variant="back" href="/providers"><BackArrowIcon />Back to Providers</Link>}
      >                
        <Formik initialValues={{}} onSubmit={submitCreate} validationSchema={_validate}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">   
              <Tabs
                vertical
                stickyFooter
                footer={<Flex sx={{gap: "10px"}}>
                  <Link href="/providers" sx={{...themevals.buttons.secondaryOutline, textDecoration: "none", margin:".25rem 0 .25rem 0"}}>Cancel</Link>
                  <BasicButton 
                    styles={{margin:".25rem 10px .25rem 10px"}}
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save Provider
                  </BasicButton>
                </Flex>}
              >
                <Tab itemKey="general">
                  <Grid gap={6} columns={[1, '1fr 3fr']} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>Basic Info</H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>Add Provider Name and Phone.</Subtext>
                    </Box> 
                    <Box>
                      <Flex sx={{gap: "1rem"}}>    
                        <TextInput                        
                          name="name"
                          label="Provider Name"
                          value={values.name}                        
                          onChange={(val: any) => setFieldValue("name",val)}
                          $errors={errors.name && submitCount > 0 ? errors.name : null}    
                          $customStyles={{flexGrow: 2}}
                        />                                               
                        <TextInput                        
                          name="phone"
                          label="Phone"
                          value={values.phone}                        
                          onChange={(val: any) => setFieldValue("phone",val)}
                          $errors={errors?.phone && submitCount > 0 ? errors?.phone : null}   
                          $customStyles={{flexGrow: 1}} 
                        />
                      </Flex>   
                    </Box>     
                  </Grid>  
                  <Separator />
                  <Grid gap={6} columns={[1, '1fr 3fr']} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>Address</H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>Add Provider Address.</Subtext>
                    </Box>        
                    <Box>
                      <Flex sx={{gap: "1rem"}}>    
                        <TextInput 
                          name="address[line1]"
                          label="Address 1"
                          value={values?.address?.line1}
                          onChange={(val: any) => setFieldValue("address.line1",val)}
                          $customStyles={{flexGrow: 2}}
                        /> 
                        <TextInput 
                          name="address[line2]"
                          label="Address 2"
                          value={values?.address?.line2}
                          onChange={(val: any) => setFieldValue("address.line2",val)}
                          $customStyles={{flexGrow: 1}}
                        /> 
                      </Flex>
                      <Flex sx={{gap: "1rem"}}>    
                        <TextInput 
                          name="address[city]"
                          label="City"
                          value={values?.address?.city}
                          onChange={(val: any) => setFieldValue("address.city",val)}
                          $customStyles={{width: "33.3333%"}}
                        /> 
                        <SelectInput 
                          name="address[state]"
                          label="State"
                          value={values?.address?.state}
                          options={countryStates}
                          onChange={(val: any) => setFieldValue("address.state",val)}
                          clearable
                          $customStyles={{width: "33.3333%"}}
                        />
                        <TextInput 
                          name="address[postalCode]"
                          label="Zip"
                          value={values?.address?.postalCode}
                          onChange={(val: any) => setFieldValue("address.postalCode",val)}
                          $customStyles={{width: "33.3333%"}}
                        />
                      </Flex>
                    </Box>
                  </Grid> 
                  <Separator />
                  <Grid gap={6} columns={[1, '1fr 3fr']} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>Email</H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>Add preferred emails for the provider.</Subtext>
                    </Box>        
                    <Box>
                      <Grid gap={3} columns={[1,2]}> 
                        <TextInput   
                          type="email"                     
                          name="contactEmail"
                          label="Contact Email"
                          value={values?.contactEmail}                        
                          onChange={(val: any) => setFieldValue("contactEmail",val)}
                          $errors={errors?.contactEmail && submitCount > 0 ? errors?.contactEmail : null}    
                        />
                        <TextInput                        
                          type="email"                     
                          name="billingEmail"
                          label="Billing Email"
                          value={values?.billingEmail}                        
                          onChange={(val: any) => setFieldValue("billingEmail",val)}
                          $errors={errors?.billingEmail && submitCount > 0 ? errors?.billingEmail : null}    
                        /> 
                      </Grid>
                    </Box>
                  </Grid>                     
                </Tab>                   
              </Tabs>                                        
            </Form>
            )
          }
        </Formik>
      </Page>
    </>
  );
}
